import React, { Component } from 'react';
import { getUserProfile, getUserCategories, createCategory, deleteCategory, getUserCards, createCard, deleteCard, updateCategory } from '../../util/APIUtils';
import { Avatar, Tabs, List, Button, Modal, Input, Select, Card, Popconfirm, message, Tag } from 'antd';
import { PlusOutlined, DeleteOutlined, CreditCardOutlined, BankOutlined, EditOutlined } from '@ant-design/icons';
import { getAvatarColor } from '../../util/Colors';
import { formatDate } from '../../util/Helpers';
import LoadingIndicator  from '../../common/LoadingIndicator';
import './Profile.scss';
import NotFound from '../../common/NotFound';
import ServerError from '../../common/ServerError';
import { getUserTimezone, setUserTimezone } from '../../util/timezone';
import { CATEGORY_ICONS } from '../../constants/icons';

const TabPane = Tabs.TabPane;
const { Option } = Select;

const CARD_TYPES = {
    CREDIT: 'Credit',
    DEBIT: 'Debit',
    CHECKING: 'Checking Account',
    HSA: 'HSA'
};

const CARD_TYPE_COLORS = {
    CREDIT: 'blue',
    DEBIT: 'green',
    CHECKING: 'orange',
    HSA: 'purple'
};

class Profile extends Component {
    constructor(props) {
        super(props);
        this.state = {
            user: null,
            isLoading: false,
            categories: [],
            cards: [],
            isCategoryModalVisible: false,
            isEditMode: false,
            editingCategoryId: null,
            isCardModalVisible: false,
            newCategoryName: '',
            newCategoryIcon: 'CoffeeOutlined',
            iconSearchText: '',
            newCardName: '',
            newCardType: 'CREDIT',
            categoriesLoading: false,
            cardsLoading: false,
            timezone: getUserTimezone()
        }
        this.loadUserProfile = this.loadUserProfile.bind(this);
        this.loadCategories = this.loadCategories.bind(this);
        this.loadCards = this.loadCards.bind(this);
    }

    loadUserProfile(username) {
        this.setState({
            isLoading: true
        });

        getUserProfile(username)
        .then(response => {
            this.setState({
                user: response,
                isLoading: false
            });
        }).catch(error => {
            if(error.status === 404) {
                this.setState({
                    notFound: true,
                    isLoading: false
                });
            } else {
                this.setState({
                    serverError: true,
                    isLoading: false
                });        
            }
        });        
    }

    loadCategories(username) {
        this.setState({ categoriesLoading: true });
        getUserCategories(username)
            .then(response => {
                this.setState({
                    categories: response || [],
                    categoriesLoading: false
                });
            })
            .catch(error => {
                console.error('Failed to load categories:', error);
                this.setState({ categoriesLoading: false });
            });
    }

    loadCards(username) {
        this.setState({ cardsLoading: true });
        getUserCards(username)
            .then(response => {
                this.setState({
                    cards: response || [],
                    cardsLoading: false
                });
            })
            .catch(error => {
                console.error('Failed to load cards:', error);
                this.setState({ cardsLoading: false });
            });
    }

    componentDidMount() {
        const username = this.props.match.params.username;
        this.loadUserProfile(username);
        this.loadCategories(username);
        this.loadCards(username);
    }

    componentWillReceiveProps(nextProps) {
        if(this.props.match.params.username !== nextProps.match.params.username) {
            this.loadUserProfile(nextProps.match.params.username);
            this.loadCategories(nextProps.match.params.username);
            this.loadCards(nextProps.match.params.username);
        }
    }

    // Category methods
    showCategoryModal = () => {
        this.setState({ 
            isCategoryModalVisible: true,
            isEditMode: false,
            newCategoryName: '',
            newCategoryIcon: 'CoffeeOutlined',
            iconSearchText: ''
        });
    }

    showEditCategoryModal = (category) => {
        this.setState({ 
            isCategoryModalVisible: true,
            isEditMode: true,
            editingCategoryId: category.id,
            newCategoryName: category.name,
            newCategoryIcon: category.icon || 'CoffeeOutlined',
            iconSearchText: ''
        });
    }

    handleCategoryOk = () => {
        const { newCategoryName, newCategoryIcon, isEditMode, editingCategoryId } = this.state;
        const trimmedName = newCategoryName.trim();
        if (!trimmedName) {
            message.warning('Please enter a category name');
            return;
        }

        const categoryData = { name: trimmedName, icon: newCategoryIcon };
        const promise = isEditMode 
            ? updateCategory(editingCategoryId, categoryData)
            : createCategory(categoryData);

        promise
            .then(() => {
                message.success(`Category ${isEditMode ? 'updated' : 'created'} successfully!`);
                this.setState({
                    isCategoryModalVisible: false,
                    newCategoryName: '',
                    newCategoryIcon: 'CoffeeOutlined',
                    iconSearchText: '',
                    isEditMode: false,
                    editingCategoryId: null
                });
                this.loadCategories(this.props.match.params.username);
            })
            .catch(error => {
                message.error(`Failed to ${isEditMode ? 'update' : 'create'} category`);
                console.error(error);
            });
    }

    handleCategoryCancel = () => {
        this.setState({
            isCategoryModalVisible: false,
            newCategoryName: '',
            newCategoryIcon: 'CoffeeOutlined',
            iconSearchText: '',
            isEditMode: false,
            editingCategoryId: null
        });
    }

    handleDeleteCategory = (categoryId) => {
        deleteCategory(categoryId)
            .then(() => {
                message.success('Category deleted successfully!');
                this.loadCategories(this.props.match.params.username);
            })
            .catch(error => {
                message.error('Failed to delete category');
                console.error(error);
            });
    }

    // Card methods
    showCardModal = () => {
        this.setState({ isCardModalVisible: true });
    }

    handleCardOk = () => {
        const { newCardName, newCardType } = this.state;
        if (!newCardName.trim()) {
            message.warning('Please enter a card name');
            return;
        }

        createCard({
            name: newCardName,
            cardType: newCardType
        })
            .then(() => {
                message.success('Card created successfully!');
                this.setState({
                    isCardModalVisible: false,
                    newCardName: '',
                    newCardType: 'CREDIT'
                });
                this.loadCards(this.props.match.params.username);
            })
            .catch(error => {
                message.error('Failed to create card');
                console.error(error);
            });
    }

    handleCardCancel = () => {
        this.setState({
            isCardModalVisible: false,
            newCardName: '',
            newCardType: 'CREDIT'
        });
    }

    handleDeleteCard = (cardId) => {
        deleteCard(cardId)
            .then(() => {
                message.success('Card deleted successfully!');
                this.loadCards(this.props.match.params.username);
            })
            .catch(error => {
                message.error('Failed to delete card');
                console.error(error);
            });
    }

    // Timezone methods
    handleTimezoneChange = (value) => {
        this.setState({ timezone: value });
        setUserTimezone(value);
        message.success('Timezone updated successfully! Refreshing page to apply changes...');

        // Reload page after a short delay to apply timezone changes
        setTimeout(() => {
            window.location.reload();
        }, 1000);
    }

    render() {
        if(this.state.isLoading) {
            return <LoadingIndicator />;
        }

        if(this.state.notFound) {
            return <NotFound />;
        }

        if(this.state.serverError) {
            return <ServerError />;
        }

        const tabBarStyle = {
            textAlign: 'center'
        };

        const { categories, cards, categoriesLoading, cardsLoading, timezone } = this.state;

        const TIMEZONE_OPTIONS = [
            { value: 'America/New_York', label: 'Eastern Time (EST/EDT)' },
            { value: 'America/Chicago', label: 'Central Time (CST/CDT)' },
            { value: 'America/Denver', label: 'Mountain Time (MST/MDT)' },
            { value: 'America/Los_Angeles', label: 'Pacific Time (PST/PDT)' },
            { value: 'America/Phoenix', label: 'Arizona Time (MST)' },
            { value: 'America/Anchorage', label: 'Alaska Time (AKST/AKDT)' },
            { value: 'Pacific/Honolulu', label: 'Hawaii Time (HST)' },
        ];

        return (
            <div className="profile">
                { 
                    this.state.user ? (
                        <div className="user-profile">
                            <div className="profile-header">
                                <Avatar className="profile-avatar" style={{ backgroundColor: getAvatarColor(this.state.user.name)}}>
                                    {this.state.user.name[0].toUpperCase()}
                                </Avatar>
                                <div className="profile-info">
                                    <h1 className="profile-name">{this.state.user.name}</h1>
                                    <p className="profile-username">@{this.state.user.username}</p>
                                    <p className="profile-joined">Joined {formatDate(this.state.user.joinedAt)}</p>
                                </div>
                            </div>
                            
                            <div className="profile-content">
                                <Tabs defaultActiveKey="1" 
                                    animated={false}
                                    size="large"
                                    className="profile-tabs">
                                    <TabPane tab={`Categories (${categories.length})`} key="1">
                                        <div className="tab-pane">
                                            <div className="tab-header">
                                                <h2>Manage Categories</h2>
                                                <Button
                                                    type="primary"
                                                    icon={<PlusOutlined />}
                                                    onClick={this.showCategoryModal}
                                                    size="large"
                                                >
                                                    Add Category
                                                </Button>
                                            </div>

                                            <div className="categories-grid">
                                                {categoriesLoading ? (
                                                    <LoadingIndicator />
                                                ) : categories.length === 0 ? (
                                                    <div className="empty-state">
                                                        <p>No categories yet</p>
                                                        <Button type="primary" onClick={this.showCategoryModal}>Create your first category</Button>
                                                    </div>
                                                ) : (
                                                    categories.map(item => (
                                                        <div key={item.id} className="category-card">
                                                            <div className="category-icon">
                                                                {item.icon ? CATEGORY_ICONS.find(i => i.value === item.icon)?.label.split(' ')[0] : '📁'}
                                                            </div>
                                                            <div className="category-name">{item.name}</div>
                                                            <div className="category-actions">
                                                                <Button
                                                                    type="text"
                                                                    onClick={() => this.showEditCategoryModal(item)}
                                                                    size="small"
                                                                >
                                                                    <EditOutlined />
                                                                </Button>
                                                                <Popconfirm
                                                                    title="Delete this category?"
                                                                    onConfirm={() => this.handleDeleteCategory(item.id)}
                                                                    okText="Yes"
                                                                    cancelText="No"
                                                                >
                                                                    <Button
                                                                        type="text"
                                                                        danger
                                                                        size="small"
                                                                    >
                                                                        <DeleteOutlined />
                                                                    </Button>
                                                                </Popconfirm>
                                                            </div>
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                        </div>
                                    </TabPane>
                                    <TabPane tab={`Cards (${cards.length})`}  key="2">
                                        <div className="tab-pane">
                                            <div className="tab-header">
                                                <h2>Payment Methods</h2>
                                                <Button
                                                    type="primary"
                                                    icon={<PlusOutlined />}
                                                    onClick={this.showCardModal}
                                                    size="large"
                                                >
                                                    Add Card
                                                </Button>
                                            </div>

                                            <div className="cards-grid">
                                                {cardsLoading ? (
                                                    <LoadingIndicator />
                                                ) : cards.length === 0 ? (
                                                    <div className="empty-state">
                                                        <p>No payment methods yet</p>
                                                        <Button type="primary" onClick={this.showCardModal}>Add your first card</Button>
                                                    </div>
                                                ) : (
                                                    cards.map(item => (
                                                        <div key={item.id} className="payment-card">
                                                            <div className="card-header">
                                                                <div className="card-icon">
                                                                    {item.cardType === 'CHECKING' || item.cardType === 'HSA'
                                                                        ? <BankOutlined />
                                                                        : <CreditCardOutlined />}
                                                                </div>
                                                                <Tag color={CARD_TYPE_COLORS[item.cardType]}>
                                                                    {CARD_TYPES[item.cardType]}
                                                                </Tag>
                                                            </div>
                                                            <div className="card-name">{item.name}</div>
                                                            <div className="card-actions">
                                                                <Popconfirm
                                                                    title="Delete this card?"
                                                                    onConfirm={() => this.handleDeleteCard(item.id)}
                                                                    okText="Yes"
                                                                    cancelText="No"
                                                                >
                                                                    <Button
                                                                        type="text"
                                                                        danger
                                                                        icon={<DeleteOutlined />}
                                                                        size="small"
                                                                    >
                                                                        Delete
                                                                    </Button>
                                                                </Popconfirm>
                                                            </div>
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                        </div>
                                    </TabPane>
                                    <TabPane tab="Settings" key="3">
                                        <div className="tab-pane">
                                            <div className="settings-section">
                                                <h2>Timezone</h2>
                                                <p className="settings-description">
                                                    Select your timezone for displaying dates and times.
                                                </p>
                                                <Select
                                                    value={timezone}
                                                    onChange={this.handleTimezoneChange}
                                                    style={{ width: '100%', maxWidth: '400px' }}
                                                    size="large"
                                                >
                                                    {TIMEZONE_OPTIONS.map(tz => (
                                                        <Option key={tz.value} value={tz.value}>
                                                            {tz.label}
                                                        </Option>
                                                    ))}
                                                </Select>
                                                <div className="settings-note">
                                                    ℹ️ Timezone preference is saved locally in your browser
                                                </div>
                                            </div>
                                        </div>
                                    </TabPane>
                                </Tabs>
                            </div>

                            {/* Category Modal */}
                            <Modal
                                title={this.state.isEditMode ? "Edit Category" : "Add New Category"}
                                visible={this.state.isCategoryModalVisible}
                                onOk={this.handleCategoryOk}
                                onCancel={this.handleCategoryCancel}
                                okText={this.state.isEditMode ? "Update" : "Create"}
                                width={600}
                            >
                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>Category Name</label>
                                    <Input
                                        placeholder="Enter category name"
                                        value={this.state.newCategoryName}
                                        onChange={(e) => this.setState({ newCategoryName: e.target.value })}
                                        style={{ fontSize: '16px' }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>Icon</label>
                                    <Input
                                        placeholder="Search icons..."
                                        value={this.state.iconSearchText}
                                        onChange={(e) => this.setState({ iconSearchText: e.target.value })}
                                        style={{ marginBottom: '8px' }}
                                    />
                                    <div style={{ 
                                        maxHeight: '300px', 
                                        overflowY: 'auto', 
                                        border: '1px solid #d9d9d9', 
                                        borderRadius: '4px',
                                        padding: '8px'
                                    }}>
                                        <div style={{ 
                                            display: 'grid', 
                                            gridTemplateColumns: 'repeat(5, 1fr)', 
                                            gap: '8px' 
                                        }}>
                                            {CATEGORY_ICONS
                                                .filter(icon => 
                                                    icon.search.toLowerCase().includes(this.state.iconSearchText.toLowerCase())
                                                )
                                                .map(icon => (
                                                    <div
                                                        key={icon.value}
                                                        onClick={() => this.setState({ newCategoryIcon: icon.value })}
                                                        style={{
                                                            padding: '12px',
                                                            textAlign: 'center',
                                                            cursor: 'pointer',
                                                            border: this.state.newCategoryIcon === icon.value ? '2px solid #1890ff' : '1px solid #d9d9d9',
                                                            borderRadius: '4px',
                                                            background: this.state.newCategoryIcon === icon.value ? '#e6f7ff' : 'white',
                                                            fontSize: '28px',
                                                            transition: 'all 0.2s'
                                                        }}
                                                        onMouseEnter={(e) => e.currentTarget.style.background = '#f0f0f0'}
                                                        onMouseLeave={(e) => {
                                                            if (this.state.newCategoryIcon !== icon.value) {
                                                                e.currentTarget.style.background = 'white';
                                                            }
                                                        }}
                                                    >
                                                        {icon.label}
                                                    </div>
                                                ))
                                            }
                                        </div>
                                    </div>
                                </div>
                            </Modal>

                            {/* Card Modal */}
                            <Modal
                                title="Add New Card"
                                visible={this.state.isCardModalVisible}
                                onOk={this.handleCardOk}
                                onCancel={this.handleCardCancel}
                                okText="Create"
                            >
                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>Card Name</label>
                                    <Input
                                        placeholder="Enter card name"
                                        value={this.state.newCardName}
                                        onChange={(e) => this.setState({ newCardName: e.target.value })}
                                        style={{ fontSize: '16px' }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>Card Type</label>
                                    <Select
                                        value={this.state.newCardType}
                                        onChange={(value) => this.setState({ newCardType: value })}
                                        style={{ width: '100%' }}
                                    >
                                        {Object.keys(CARD_TYPES).map(key => (
                                            <Option key={key} value={key}>{CARD_TYPES[key]}</Option>
                                        ))}
                                    </Select>
                                </div>
                            </Modal>
                        </div>
                    ): null               
                }
            </div>
        );
    }
}

export default Profile;